import { RefObject, useCallback, useEffect } from 'react';

type Modifier = 'altKey' | 'ctrlKey' | 'metaKey' | 'shiftKey';

const checkModifier = (event: KeyboardEvent, modifier?: Modifier) =>
  modifier ? event[modifier] : true;

export const useKeyEvent = (
  key: string,
  callback: (e: KeyboardEvent) => void,
  options?: {
    capture?: boolean;
    preventDefault?: boolean;
    ref?: RefObject<HTMLInputElement>;
    modifier?: Modifier;
  },
): void => {
  const { capture = false, preventDefault = true, ref, modifier } = options ?? {};

  const handleKeyDown = useCallback(
    (event: KeyboardEvent) => {
      if (
        event.key === key &&
        checkModifier(event, modifier) &&
        (!ref || (ref.current && ref.current.contains(event.target as Node)))
      ) {
        if (preventDefault) {
          event.preventDefault();
        }
        callback(event);
      }
    },
    [callback, key, modifier, preventDefault, ref],
  );

  useEffect(() => {
    window.addEventListener('keydown', handleKeyDown, { capture });

    return () => {
      window.removeEventListener('keydown', handleKeyDown, { capture });
    };
  }, [callback, handleKeyDown, capture]);
};
